import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
  Paper, Typography, alpha, useTheme,
} from '@mui/material';
import type { InventoryItemWithBalance, BulkActiveResult } from '../../services/inventoryItemService';

/**
 * ITEM-ACTIVE.4 — "retire the ones that are out of stock", as one decision.
 *
 * Clearing a shelf is not forty decisions, it is one decision about forty
 * items, and the old selection mode made you express it forty times before it
 * would act. This states the situation in words, proposes the whole set, and
 * lets you take things OUT of it — which is the shape the decision actually
 * has.
 *
 * NOTHING COMMITS UNTIL APPLY. The panel is a proposal; the list underneath is
 * untouched while it is open. That is what earns the absence of a confirm
 * dialog: the review IS the confirmation, and it names every item it is about
 * to touch.
 */

export interface RetireSweepBannerProps {
  /** Active items with no stock — the candidates. */
  candidates: InventoryItemWithBalance[];
  /** Active items that are out of stock but hold a non-zero balance anyway. */
  blocked: InventoryItemWithBalance[];
  canEdit: boolean;
  busy: boolean;
  /** Returns the server's per-item verdict so refusals can be named. */
  onApply: (itemIds: string[]) => Promise<BulkActiveResult | null>;
}

const RetireSweepBanner: React.FC<RetireSweepBannerProps> = ({
  candidates, blocked, canEdit, busy, onApply,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [skipped, setSkipped] = useState<BulkActiveResult['skipped']>([]);

  // Reopening starts from the whole set again: an exclusion made last time is
  // an answer to a question nobody asked twice.
  useEffect(() => { if (open) setExcluded(new Set()); }, [open]);

  if (candidates.length === 0 && skipped.length === 0) return null;

  const picked = candidates.filter(c => !excluded.has(c._id));

  const toggle = (id: string) => setExcluded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const apply = async () => {
    const result = await onApply(picked.map(c => c._id));
    setSkipped(result?.skipped ?? []);
    setOpen(false);
  };

  return (
    <>
      {skipped.length > 0 && (
        <Alert severity="warning" onClose={() => setSkipped([])}>
          {skipped.length} item{skipped.length === 1 ? ' was' : 's were'} left alone:
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
            {skipped.map(sk => (
              <li key={sk.itemId}>
                <strong>{sk.displayName || sk.itemId}</strong> — {sk.message}
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {candidates.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{candidates.length} item{candidates.length === 1 ? '' : 's'}</strong>
            {candidates.length === 1 ? ' is' : ' are'} out of stock.
          </Typography>
          {canEdit && !open && (
            <Button size="small" onClick={() => setOpen(true)} sx={{ textTransform: 'none' }}>
              Review
            </Button>
          )}
        </Paper>
      )}

      {open && canEdit && (
        <Paper
          elevation={0}
          sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Retire these?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Nothing changes until you press the button. Untick anything that should stay.
          </Typography>

          <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 1 }}>
            {candidates.map(c => (
              <Box
                key={c._id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  borderBottom: '1px solid', borderColor: 'divider', py: 0.25,
                }}
              >
                <FormControlLabel
                  sx={{ flex: 1, minWidth: 0, mr: 0 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={!excluded.has(c._id)}
                      onChange={() => toggle(c._id)}
                      inputProps={{ 'aria-label': `Retire ${c.displayName}` }}
                    />
                  )}
                  label={(
                    <Typography variant="body2" noWrap title={c.displayName ?? ''}>
                      {c.displayName}
                    </Typography>
                  )}
                />
                <Chip
                  label="Out"
                  size="small"
                  sx={{
                    fontSize: '0.68rem', height: 20, fontWeight: 600, border: 'none',
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.1),
                  }}
                />
              </Box>
            ))}
          </Box>

          {blocked.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {blocked.length} item{blocked.length === 1 ? '' : 's'} still hold stock and
                {blocked.length === 1 ? ' is' : ' are'} not included:
              </Typography>
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                {blocked.map(b => (
                  <li key={b._id}>
                    <Typography variant="caption" color="text.secondary">
                      {b.displayName} — {b.balance?.totalQuantity ?? 0} on hand
                    </Typography>
                  </li>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
            {busy && <CircularProgress size={16} />}
            <Button size="small" onClick={() => setOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={apply}
              disabled={busy || picked.length === 0}
              sx={{ textTransform: 'none' }}
            >
              Retire {picked.length} item{picked.length === 1 ? '' : 's'}
            </Button>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default RetireSweepBanner;
